import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useMemo, useState } from 'react'

import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

import { ONCALL_QUERY_KEYS } from '@/modules/Oncall/constants'
import {
    getOncallAssignments,
    getOncallSchedules,
    getScheduleRotations,
} from '@/modules/Oncall/services'
import { Rotation } from '@/modules/Oncall/types/rotation'
import {
    OncallAssignmentResponse,
    OncallScheduleResponse,
} from '@/modules/Oncall/types/response'
import {
    getMemberForDay,
    getSlotsForDay,
} from '@/modules/Oncall/components/Schedules/ScheduleEditor/RightPanel/timeline-utils'
import type { AssignmentSpan, ScheduleColor, ScheduleGroup } from './types'
import { LABEL_WIDTH, MIN_COL_WIDTH } from './types'

dayjs.extend(isoWeek)

function isFixedSchedule(schedule: OncallScheduleResponse): boolean {
    return schedule.oncall_schedule_metadata?.is_fixed_schedule === true
}

function toScheduleIds(schedules: OncallScheduleResponse[]): number[] {
    return schedules.map((schedule) => Number(schedule.oncall_schedule_id))
}

function extractTime(timestamp: string): string {
    if (!timestamp) return '00:00'
    const timePart = timestamp.includes('T')
        ? timestamp.split('T')[1]
        : timestamp
    return timePart?.slice(0, 5) || '00:00'
}

export function getColor(id: number): ScheduleColor {
    const hue = (id * 45) % 360
    return {
        bg: `hsl(${hue}, 70%, 85%)`,
        border: `hsl(${hue}, 70%, 60%)`,
        text: `hsl(${hue}, 70%, 30%)`,
    }
}

function buildAssignmentSpans(
    assignmentList: OncallAssignmentResponse[],
    days: Dayjs[],
    scheduleId: number,
    rotationId?: number
): AssignmentSpan[] {
    const spans: AssignmentSpan[] = []
    let index = 0

    while (index < days.length) {
        const dayStr = days[index].format('YYYY-MM-DD')
        const dayAssignment = assignmentList.find(
            (assignment) => assignment.oncall_date === dayStr
        )

        if (!dayAssignment) {
            spans.push({
                memberName: null,
                startIdx: index,
                colSpan: 1,
                timeFrom: '',
                timeTo: '',
                scheduleId,
                rotationId,
            })
            index++
            continue
        }

        const memberName = dayAssignment.employee_name
        const timeFrom = extractTime(dayAssignment.oncall_start_time)
        const timeTo = extractTime(dayAssignment.oncall_end_time)
        const isTentative =
            dayAssignment.oncall_assignment_metadata?.is_tentative === true
        let colSpan = 1

        while (index + colSpan < days.length) {
            const nextDayStr = days[index + colSpan].format('YYYY-MM-DD')
            const nextAssignment = assignmentList.find(
                (assignment) => assignment.oncall_date === nextDayStr
            )
            if (
                !nextAssignment ||
                nextAssignment.employee_name !== memberName ||
                nextAssignment.oncall_assignment_metadata?.is_tentative !==
                    isTentative
            ) {
                break
            }
            colSpan++
        }

        spans.push({
            memberName,
            startIdx: index,
            colSpan,
            timeFrom,
            timeTo,
            scheduleId,
            rotationId,
            isTentative,
        })
        index += colSpan
    }

    return spans
}

function buildOfficialAssignmentKey(
    scheduleId: number,
    rotationId: number | undefined,
    date: string
): string {
    return `${scheduleId}:${rotationId || 0}:${date}`
}

function createTentativeAssignment(params: {
    schedule: OncallScheduleResponse
    rotation: Rotation
    date: string
    memberName: string
    timeFrom: string
    timeTo: string
}): OncallAssignmentResponse {
    return {
        oncall_assignment_id: -Number(
            `${params.rotation.oncall_rotation_id}${dayjs(params.date).format('YYYYMMDD')}`
        ),
        oncall_schedule_id: params.schedule.oncall_schedule_id,
        oncall_schedule_name: params.schedule.oncall_schedule_name,
        oncall_rotation_id: params.rotation.oncall_rotation_id,
        oncall_rotation_name: params.rotation.oncall_rotation_name,
        employee_id: 0,
        employee_name: params.memberName,
        oncall_date: params.date,
        oncall_start_time: `${params.date}T${params.timeFrom}:00`,
        oncall_end_time: `${params.date}T${params.timeTo}:00`,
        oncall_assignment_status: 'scheduled',
        original_employee_id: null,
        original_employee_name: null,
        reassignment_reason: null,
        notification_sent: false,
        handoff_notification_sent: false,
        oncall_assignment_metadata: { is_tentative: true },
        rotation_order: params.rotation.oncall_rotation_order,
        created_by: null,
        created_by_name: null,
        updated_by: null,
        created_at: '',
        updated_at: '',
    } as OncallAssignmentResponse
}

function buildTentativeAssignments(
    schedules: OncallScheduleResponse[],
    rotationsBySchedule: Record<number, Rotation[]>,
    officialAssignments: OncallAssignmentResponse[],
    days: Dayjs[],
    dayDates: Date[],
    firstTentativeDate: string
): OncallAssignmentResponse[] {
    const officialKeys = new Set(
        officialAssignments.map((assignment) =>
            buildOfficialAssignmentKey(
                assignment.oncall_schedule_id,
                assignment.oncall_rotation_id,
                assignment.oncall_date
            )
        )
    )
    const firstTentativeDay = dayjs(firstTentativeDate).startOf('day')

    return schedules.flatMap((schedule) => {
        const rotations = rotationsBySchedule[schedule.oncall_schedule_id] || []

        return rotations.flatMap((rotation) => {
            if (!rotation.is_active || rotation.member_count <= 0) return []

            return days.flatMap((day, dayIndex) => {
                if (day.startOf('day').isBefore(firstTentativeDay)) return []

                const date = day.format('YYYY-MM-DD')
                const key = buildOfficialAssignmentKey(
                    schedule.oncall_schedule_id,
                    rotation.oncall_rotation_id,
                    date
                )
                if (officialKeys.has(key)) return []

                const slots = getSlotsForDay(rotation, day.toDate())
                if (slots.length === 0) return []

                const memberName = getMemberForDay(rotation, dayIndex, dayDates)
                if (!memberName) return []

                return createTentativeAssignment({
                    schedule,
                    rotation,
                    date,
                    memberName,
                    timeFrom: slots[0].time_from,
                    timeTo: slots[0].time_to,
                })
            })
        })
    })
}

function buildScheduleGroups(
    schedules: OncallScheduleResponse[],
    assignments: OncallAssignmentResponse[],
    showEmptySchedules: boolean
): ScheduleGroup[] {
    const grouped: Record<number, ScheduleGroup> = {}

    schedules.forEach((schedule) => {
        grouped[schedule.oncall_schedule_id] = {
            scheduleId: schedule.oncall_schedule_id,
            scheduleName: schedule.oncall_schedule_name,
            rotations: {},
            legacyAssignments: [],
        }
    })

    assignments.forEach((assignment) => {
        const scheduleId = assignment.oncall_schedule_id
        const schedule = grouped[scheduleId]
        if (!schedule) return

        if (assignment.oncall_rotation_id) {
            const rotationId = assignment.oncall_rotation_id
            if (!schedule.rotations[rotationId]) {
                schedule.rotations[rotationId] = {
                    rotationId,
                    rotationName: assignment.oncall_rotation_name || 'Default',
                    assignments: [],
                }
            }
            schedule.rotations[rotationId].assignments.push(assignment)
            return
        }

        schedule.legacyAssignments.push(assignment)
    })

    return Object.values(grouped).filter(
        (schedule) =>
            showEmptySchedules ||
            Object.keys(schedule.rotations).length > 0 ||
            schedule.legacyAssignments.length > 0
    )
}

export function useDashboardTimeline(
    defaultDays: number,
    departmentId?: number,
    weekMode: 'calendar' | 'rolling' = 'rolling',
    showEmptySchedules = false
) {
    const [dateOffset, setDateOffset] = useState(0)
    const isCalendarMode = weekMode === 'calendar'

    const dateRange = useMemo(() => {
        const start =
            weekMode === 'calendar'
                ? dayjs().add(dateOffset, 'week').startOf('isoWeek')
                : dayjs()
                      .add(dateOffset * defaultDays, 'day')
                      .startOf('day')
        const end =
            weekMode === 'calendar'
                ? start.endOf('isoWeek')
                : start.add(defaultDays - 1, 'day').endOf('day')

        return {
            start: start.format('YYYY-MM-DD'),
            end: end.format('YYYY-MM-DD'),
        }
    }, [dateOffset, defaultDays, weekMode])

    const days = useMemo(() => {
        const dayCount =
            weekMode === 'calendar'
                ? dayjs(dateRange.end).diff(dayjs(dateRange.start), 'day') + 1
                : defaultDays

        return Array.from({ length: dayCount }, (_, index) =>
            dayjs(dateRange.start).add(index, 'day')
        )
    }, [dateRange.start, dateRange.end, defaultDays, weekMode])

    const normalEndDate = useMemo(() => {
        if (!isCalendarMode) {
            return dateRange.end
        }

        const tomorrow = dayjs().add(1, 'day').startOf('day')
        const rangeEnd = dayjs(dateRange.end).startOf('day')

        return (tomorrow.isBefore(rangeEnd) ? tomorrow : rangeEnd).format(
            'YYYY-MM-DD'
        )
    }, [dateRange.end, isCalendarMode])

    const canFetchNormalAssignments = normalEndDate >= dateRange.start

    const { data: schedulesData, isLoading: isLoadingSchedules } =
        useQueryWithHandler({
            queryKey: [
                ONCALL_QUERY_KEYS.SCHEDULES,
                'dashboard-timeline',
                departmentId,
                weekMode,
            ],
            queryFn: () =>
                getOncallSchedules({
                    page: 1,
                    page_size: 500,
                    is_active: true,
                    ...(departmentId ? { department_id: departmentId } : {}),
                }),
        })

    const activeSchedules = useMemo<OncallScheduleResponse[]>(() => {
        const schedules = schedulesData?.results || []
        return schedules.filter(
            (schedule: OncallScheduleResponse) =>
                schedule.oncall_schedule_active
        )
    }, [schedulesData])

    const fixedSchedules = useMemo(
        () => (isCalendarMode ? activeSchedules.filter(isFixedSchedule) : []),
        [activeSchedules, isCalendarMode]
    )

    const normalSchedules = useMemo(
        () =>
            isCalendarMode
                ? activeSchedules.filter(
                      (schedule) => !isFixedSchedule(schedule)
                  )
                : activeSchedules,
        [activeSchedules, isCalendarMode]
    )

    const fixedScheduleIds = useMemo(
        () => toScheduleIds(fixedSchedules),
        [fixedSchedules]
    )

    const normalScheduleIds = useMemo(
        () => toScheduleIds(normalSchedules),
        [normalSchedules]
    )

    const dayDates = useMemo(() => days.map((day) => day.toDate()), [days])

    const { data: normalRotationsData, isLoading: isLoadingNormalRotations } =
        useQueryWithHandler({
            queryKey: [
                ONCALL_QUERY_KEYS.ROTATIONS,
                'dashboard-timeline',
                'normal',
                dateRange.start,
                dateRange.end,
                departmentId,
                weekMode,
                normalScheduleIds,
            ],
            queryFn: async () => {
                const results = await Promise.all(
                    normalSchedules.map(async (schedule) => {
                        const response = await getScheduleRotations(
                            schedule.oncall_schedule_id
                        )
                        return {
                            scheduleId: schedule.oncall_schedule_id,
                            rotations: response.results || [],
                        }
                    })
                )

                return results.reduce<Record<number, Rotation[]>>(
                    (acc, item) => {
                        acc[item.scheduleId] = item.rotations
                        return acc
                    },
                    {}
                )
            },
            enabled: isCalendarMode && normalScheduleIds.length > 0,
        })

    const { data: fixedAssignmentsData, isLoading: isLoadingFixedAssignments } =
        useQueryWithHandler({
            queryKey: [
                ONCALL_QUERY_KEYS.ASSIGNMENTS,
                'dashboard-timeline',
                'fixed',
                dateRange.start,
                dateRange.end,
                departmentId,
                weekMode,
                fixedScheduleIds,
            ],
            queryFn: () =>
                getOncallAssignments({
                    page: 1,
                    page_size: 2000,
                    start_date: dateRange.start,
                    end_date: dateRange.end,
                    schedule_ids: fixedScheduleIds,
                }),
            enabled: fixedScheduleIds.length > 0,
        })

    const {
        data: normalAssignmentsData,
        isLoading: isLoadingNormalAssignments,
    } = useQueryWithHandler({
        queryKey: [
            ONCALL_QUERY_KEYS.ASSIGNMENTS,
            'dashboard-timeline',
            'normal',
            dateRange.start,
            normalEndDate,
            departmentId,
            weekMode,
            normalScheduleIds,
        ],
        queryFn: () =>
            getOncallAssignments({
                page: 1,
                page_size: 2000,
                start_date: dateRange.start,
                end_date: normalEndDate,
                schedule_ids: normalScheduleIds,
            }),
        enabled: normalScheduleIds.length > 0 && canFetchNormalAssignments,
    })

    const assignments = useMemo<OncallAssignmentResponse[]>(
        () => [
            ...(fixedAssignmentsData?.results || []),
            ...(normalAssignmentsData?.results || []),
        ],
        [fixedAssignmentsData, normalAssignmentsData]
    )

    const firstTentativeDate = useMemo(
        () => dayjs(normalEndDate).add(1, 'day').format('YYYY-MM-DD'),
        [normalEndDate]
    )

    const tentativeAssignments = useMemo(
        () =>
            isCalendarMode
                ? buildTentativeAssignments(
                      normalSchedules,
                      normalRotationsData || {},
                      normalAssignmentsData?.results || [],
                      days,
                      dayDates,
                      firstTentativeDate
                  )
                : [],
        [
            dayDates,
            days,
            firstTentativeDate,
            isCalendarMode,
            normalAssignmentsData,
            normalRotationsData,
            normalSchedules,
        ]
    )

    const visibleAssignments = useMemo<OncallAssignmentResponse[]>(
        () => [...assignments, ...tentativeAssignments],
        [assignments, tentativeAssignments]
    )

    const scheduleData = useMemo(
        () =>
            buildScheduleGroups(
                activeSchedules,
                visibleAssignments,
                showEmptySchedules
            ),
        [activeSchedules, visibleAssignments, showEmptySchedules]
    )

    const gridCols = `${LABEL_WIDTH}px repeat(${days.length}, minmax(${MIN_COL_WIDTH}px, 1fr))`
    const minWidth = `calc(${LABEL_WIDTH}px + ${days.length} * ${MIN_COL_WIDTH}px)`

    const getSpans = (
        assignmentList: OncallAssignmentResponse[],
        scheduleId: number,
        rotationId?: number
    ) => buildAssignmentSpans(assignmentList, days, scheduleId, rotationId)

    return {
        dateRange,
        days,
        scheduleData,
        isLoading:
            isLoadingSchedules ||
            isLoadingNormalRotations ||
            isLoadingFixedAssignments ||
            isLoadingNormalAssignments,
        gridCols,
        minWidth,
        dateOffset,
        setDateOffset,
        getSpans,
        handlePrev: () => setDateOffset((prev) => prev - 1),
        handleNext: () => setDateOffset((prev) => prev + 1),
    }
}
